import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import SEOHead, { SITE_URL } from "@/components/SEOHead";
import SignupModal from "@/components/SignupModal";

const AINewsletterBeginners = () => {
  const [editions, setEditions] = useState<any[]>([]);
  const [signupOpen, setSignupOpen] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("editions")
        .select("id, slug, edition_date, hook")
        .in("status", ["approved", "sent"])
        .order("edition_date", { ascending: false })
        .limit(3);
      if (data) setEditions(data);
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="AI Newsletter for Beginners — Simple Daily Updates | aibly"
        description="New to AI? Get a beginner-friendly daily newsletter that explains artificial intelligence news in plain language. Free, simple, and 2 minutes to read."
        canonical="/ai-newsletter-for-beginners"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "AI Newsletter for Beginners — aibly",
            description: "Beginner-friendly daily AI newsletter. Simple explanations of artificial intelligence news in 2 minutes.",
            url: `${SITE_URL}/ai-newsletter-for-beginners`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "AI Newsletter for Beginners", item: `${SITE_URL}/ai-newsletter-for-beginners` },
            ],
          },
        ]}
      />
      <Navbar onSubscribeClick={() => setSignupOpen(true)} />

      <article className="pt-24 pb-16 px-4 max-w-2xl mx-auto space-y-10">
        <header className="space-y-4">
          <nav className="text-xs text-muted-foreground tracking-widest uppercase">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground">AI Newsletter for Beginners</span>
          </nav>

          <h1 className="font-display text-3xl md:text-5xl italic text-foreground leading-tight">
            The AI Newsletter That Doesn't Assume You're an Expert
          </h1>

          <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-xl">
            You don't need a CS degree. You don't need to know what "transformer architecture" means. aibly covers AI in plain language, explains the context, and gets to the point. 2 minutes, once a day.
          </p>
        </header>

        <section className="space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
            This is for you if
          </h2>
          <div className="space-y-3">
            {[
              "You keep hearing about AI but have no idea where to actually follow it",
              "Technical jargon makes your eyes glaze over",
              "You want to know how AI affects your job without reading research papers",
              "Social media AI discourse is 90% hype and 10% useful",
              "You just want someone to tell you what happened and why it matters",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 pl-4 border-l border-border">
                <span className="text-accent text-sm shrink-0">✓</span>
                <p className="text-sm text-foreground/90 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
            How it works
          </h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Most AI coverage is written for people who already work in AI. If you're not an engineer or a VC, good luck making sense of it.
            </p>
            <p>
              aibly is written differently. Every story gets context: why a new model matters, what a regulation actually changes, how a deal affects the industry. If there's a term you might not know, it gets explained right there.
            </p>
            <p>
              It shows up in your inbox at the time you choose. Read it with coffee, on the train, between meetings. Two minutes and you'll know more about AI than most people pick up in a week.
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
            Inside Each Edition
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { title: "5 to 7 stories", desc: "Plain language. Source linked. No acronym soup." },
              { title: "Stat of the Day", desc: "One number that puts the day's AI news in perspective." },
              { title: "Hot Take", desc: "An opinion you can agree or disagree with. We don't sit on the fence." },
              { title: "Friday Quiz", desc: "Questions from the week's editions. Quick, fun, slightly addictive." },
            ].map((item) => (
              <div key={item.title} className="border border-border hover:border-muted-foreground transition-colors p-4 space-y-1">
                <h3 className="text-sm font-medium text-foreground">{item.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {editions.length > 0 && (
          <section className="space-y-4">
            <h2 className="text-xs tracking-widest uppercase text-muted-foreground">
              See What You've Been Missing
            </h2>
            <div className="space-y-2">
              {editions.map((ed) => (
                <Link
                  key={ed.id}
                  to={`/edition/${ed.slug || ed.id}`}
                  className="block border border-border hover:border-muted-foreground transition-colors p-4"
                >
                  <time className="text-[10px] text-muted-foreground tracking-widest uppercase">
                    {format(new Date(ed.edition_date + "T00:00:00"), "EEEE, MMMM d, yyyy")}
                  </time>
                  {ed.hook && (
                    <p className="font-display text-foreground italic text-sm mt-1 line-clamp-1">{ed.hook}</p>
                  )}
                </Link>
              ))}
            </div>
            <Link to="/archive" className="text-xs text-accent hover:underline tracking-widest uppercase">
              Browse full archive →
            </Link>
          </section>
        )}

        <div className="border border-border p-8 text-center space-y-4">
          <p className="font-display text-foreground italic text-lg md:text-xl leading-snug">
            You don't need to be technical. You just need 2 minutes.
          </p>
          <p className="text-xs text-muted-foreground tracking-wider uppercase">
            Free · Plain language · Delivered daily
          </p>
          <Button
            onClick={() => setSignupOpen(true)}
            className="btn-shine rounded-none text-xs tracking-widest uppercase px-10 py-5 gap-2"
          >
            Subscribe Free <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </article>

      <SignupModal open={signupOpen} onOpenChange={setSignupOpen} />
    </div>
  );
};

export default AINewsletterBeginners;
